import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useJsApiLoader, GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import './RouteMap.css';
import { geocodeAddress } from '../services/geocoding';

const ISRAEL_CENTER = { lat: 32.0853, lng: 34.7818 };
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  language: 'he',
  region: 'il'
};

function RouteMap({ 
  origin,           // { address, lat, lng } or just address string
  destination,      // { address, lat, lng } or just address string
  waypoints = [],   // Array of { address, lat, lng } or address strings
  showDirections = true,
  height = '300px',
  onRouteCalculated
}) {
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mapCenter] = useState(ISRAEL_CENTER);
  const [mapZoom] = useState(8);
  const directionsServiceRef = useRef(null);
  const mapRef = useRef(null);
  const isMounted = useRef(true);

  // Serialize locations so effects only rerun when values actually change
  const originKey = useMemo(() => {
    if (!origin) return '';
    if (typeof origin === 'string') return origin;
    return `${origin.lat || ''},${origin.lng || ''}|${origin.address || ''}`;
  }, [origin]);

  const destinationKey = useMemo(() => {
    if (!destination) return '';
    if (typeof destination === 'string') return destination;
    return `${destination.lat || ''},${destination.lng || ''}|${destination.address || ''}`;
  }, [destination]);

  const waypointsKey = useMemo(() => {
    if (!waypoints || waypoints.length === 0) return '';
    return waypoints.map(wp => {
      if (typeof wp === 'string') return wp;
      return `${wp.lat || ''},${wp.lng || ''}|${wp.address || ''}`;
    }).join(';');
  }, [waypoints]);

  // Track mount state
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getLatLng = useCallback(async (location) => {
    if (!location) return null;
    
    // If already has lat/lng - validate and convert to numbers
    if (typeof location === 'object' && location.lat != null && location.lng != null) {
      const lat = typeof location.lat === 'string' ? parseFloat(location.lat) : location.lat;
      const lng = typeof location.lng === 'string' ? parseFloat(location.lng) : location.lng;
      
      // Validate that they are valid numbers
      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    
    // Geocode the address
    const address = typeof location === 'string' ? location : (location.address || location);
    if (!address || (typeof address === 'string' && address.trim() === '')) return null;

    try {
      const result = await geocodeAddress(address);
      if (result && typeof result.lat === 'number' && typeof result.lng === 'number') {
        return result;
      }
      return null;
    } catch (err) {
      return null;
    }
  }, []);

  const formatDuration = useCallback((minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    
    if (hours > 0) {
      return `${hours} שעות ${mins} דקות`;
    }
    return `${mins} דקות`;
  }, []);

  const [mapReady, setMapReady] = useState(false);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google && window.google.maps) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
      setMapReady(true);
    }
  }, []);

  // Calculate route using Google Maps Directions API
  const calculateRoute = useCallback(async () => {
    if (!directionsServiceRef.current || !isMounted.current) return;

    setIsLoading(true);
    setError(null);
    setDirectionsResponse(null);

    try {
      const originCoords = await getLatLng(origin);
      const destCoords = await getLatLng(destination);

      if (!isMounted.current) return;

      if (!originCoords) {
        setError('לא ניתן למצוא את מיקום האיסוף');
        setIsLoading(false);
        return;
      }

      if (!destCoords) {
        setError('לא ניתן למצוא את מיקום היעד');
        setIsLoading(false);
        return;
      }

      // Final validation - ensure coordinates are valid numbers
      const validateCoords = (coords) => {
        return coords && 
          typeof coords.lat === 'number' && 
          typeof coords.lng === 'number' && 
          !isNaN(coords.lat) && 
          !isNaN(coords.lng) &&
          coords.lat >= -90 && coords.lat <= 90 &&
          coords.lng >= -180 && coords.lng <= 180;
      };

      if (!validateCoords(originCoords)) {
        console.error('Invalid origin coordinates:', originCoords);
        setError('מיקום האיסוף לא תקין');
        setIsLoading(false);
        return;
      }

      if (!validateCoords(destCoords)) {
        console.error('Invalid destination coordinates:', destCoords);
        setError('מיקום היעד לא תקין');
        setIsLoading(false);
        return;
      }

      // Get waypoint coordinates
      const waypointCoords = [];
      for (const wp of waypoints) {
        const wpCoords = await getLatLng(wp);
        if (wpCoords && validateCoords(wpCoords)) {
          waypointCoords.push(wpCoords);
        }
      }

      if (!showDirections) {
        // Just show markers without route
        const allCoords = [originCoords, ...waypointCoords, destCoords];
        const bounds = new window.google.maps.LatLngBounds();
        allCoords.forEach(coord => bounds.extend(coord));
        
        if (mapRef.current) {
          mapRef.current.fitBounds(bounds);
        }
        
        setIsLoading(false);
        return;
      }

      // Build waypoints for Directions API
      const waypointsForAPI = waypointCoords.map(wp => ({
        location: { lat: Number(wp.lat), lng: Number(wp.lng) },
        stopover: true
      }));

      // Request directions with guaranteed number types
      directionsServiceRef.current.route(
        {
          origin: { lat: Number(originCoords.lat), lng: Number(originCoords.lng) },
          destination: { lat: Number(destCoords.lat), lng: Number(destCoords.lng) },
          waypoints: waypointsForAPI,
          travelMode: window.google.maps.TravelMode.DRIVING,
          language: 'he',
          region: 'il'
        },
        (result, status) => {
          if (!isMounted.current) return;

          if (status === 'OK' && result) {
            setDirectionsResponse(result);
            
            // Extract route info
            let totalDistance = 0;
            let totalDuration = 0;
            const legs = [];

            result.routes[0].legs.forEach((leg, index) => {
              totalDistance += leg.distance.value; // meters
              totalDuration += leg.duration.value; // seconds
              
              legs.push({
                start: leg.start_address,
                end: leg.end_address,
                distance: (leg.distance.value / 1000).toFixed(1) + ' ק"מ',
                duration: formatDuration(leg.duration.value / 60)
              });
            });

            const info = {
              distance: (totalDistance / 1000).toFixed(1) + ' ק"מ',
              duration: formatDuration(totalDuration / 60),
              legs
            };

            setRouteInfo(info);
            onRouteCalculated?.(info);

            // Fit bounds to route
            if (mapRef.current && result.routes[0].bounds) {
              mapRef.current.fitBounds(result.routes[0].bounds);
            }
          } else {
            console.error('Directions request failed:', status);
            setError('שגיאה בחישוב המסלול');
          }
          
          setIsLoading(false);
        }
      );

    } catch (err) {
      if (!isMounted.current) return;
      console.error('Route calculation error:', err);
      setError('שגיאה בחישוב המסלול');
      setIsLoading(false);
    }
  }, [origin, destination, waypoints, showDirections, getLatLng, formatDuration, onRouteCalculated]);

  // Recalculate route when keys change OR when map becomes ready
  useEffect(() => {
    // If directions service isn't ready yet, wait for mapReady
    if (!mapReady || !directionsServiceRef.current) {
      return;
    }

    if (origin && destination) {
      calculateRoute();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originKey, destinationKey, waypointsKey, mapReady]);

  // Get marker positions (must be before early return)
  const originCoords = useMemo(() => {
    if (!origin) return null;
    if (typeof origin === 'object' && origin.lat != null && origin.lng != null) {
      return { lat: origin.lat, lng: origin.lng };
    }
    return null;
  }, [origin]);

  const destCoords = useMemo(() => {
    if (!destination) return null;
    if (typeof destination === 'object' && destination.lat != null && destination.lng != null) {
      return { lat: destination.lat, lng: destination.lng };
    }
    return null;
  }, [destination]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: 'he',
    region: 'IL'
  });

  if (!apiKey) {
    return (
      <div className="route-map-container">
        <div className="route-error">
          <span>⚠️ מפתח Google Maps API לא הוגדר</span>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="route-map-container">
        <div className="map-loading">
          <div className="map-spinner"></div>
          <span>טוען מפה...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="route-map-container">
        <div className="route-error">
          <span>⚠️ שגיאה בטעינת המפה</span>
        </div>
      </div>
    );
  }

  return (
    <div className="route-map-container">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height }}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onMapLoad}
        options={mapOptions}
      >
        {directionsResponse && (
          <DirectionsRenderer
            directions={directionsResponse}
            options={{
              suppressMarkers: false,
              polylineOptions: {
                strokeColor: '#3b82f6',
                strokeWeight: 5,
                strokeOpacity: 0.8
              }
            }}
          />
        )}
        
        {/* Origin marker */}
        {originCoords && typeof originCoords.lat === 'number' && typeof originCoords.lng === 'number' && !isNaN(originCoords.lat) && !isNaN(originCoords.lng) && !directionsResponse && (
          <Marker
            position={originCoords}
            label={{ text: 'A', color: 'white' }}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE,
              scale: 10,
              fillColor: '#22c55e',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 2
            }}
          />
        )}
        
        {/* Destination marker */}
        {destCoords && typeof destCoords.lat === 'number' && typeof destCoords.lng === 'number' && !isNaN(destCoords.lat) && !isNaN(destCoords.lng) && !directionsResponse && (
          <Marker
            position={destCoords}
            label={{ text: 'B', color: 'white' }}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE,
              scale: 10,
              fillColor: '#ef4444',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 2
            }}
          />
        )}
      </GoogleMap>
      
      {isLoading && (
        <div className="map-loading">
          <div className="map-spinner"></div>
          <span>טוען מפה...</span>
        </div>
      )}
      
      {error && (
        <div className="route-error">
          <span>⚠️ {error}</span>
        </div>
      )}

      {routeInfo && (
        <div className="route-info">
          <div className="route-summary">
            <div className="route-stat">
              <span className="stat-icon">🚗</span>
              <span className="stat-value">{routeInfo.distance}</span>
            </div>
            <div className="route-stat">
              <span className="stat-icon">⏱️</span>
              <span className="stat-value">{routeInfo.duration}</span>
            </div>
          </div>
          
          {routeInfo.legs.length > 1 && (
            <div className="route-legs">
              {routeInfo.legs.map((leg, index) => (
                <div key={index} className="route-leg">
                  <span className="leg-number">{index + 1}</span>
                  <div className="leg-details">
                    <span className="leg-endpoints">
                      {leg.start} → {leg.end}
                    </span>
                    <span className="leg-stats">
                      {leg.distance} • {leg.duration}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RouteMap;
